import BluetoothSearchingIcon from '@mui/icons-material/BluetoothSearching';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { type FormEvent, type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router';
import { forgetSignOutReason, signOutReason, useSession } from '../auth/session';
import { BRAND_FONT, BrandLockup, BrandMark } from '../components/Brand';
import { LanguageMenu } from '../components/LanguageMenu';
import {
  type AuthError,
  type Identifier,
  parseIdentifier,
  requestCode,
  verifyCode,
} from '../lib/auth';

const RESEND_SECONDS = 60;

export function LoginPage() {
  const { t } = useTranslation();
  const { session } = useSession();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;
  const [raw, setRaw] = useState('');
  const [target, setTarget] = useState<Identifier | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<AuthError | 'invalid_identifier' | null>(null);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [signedOut] = useState(signOutReason);
  useEffect(forgetSignOutReason, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  if (session) return <Navigate to={from?.startsWith('/') ? from : '/'} replace />;

  async function send(id: Identifier) {
    setBusy(true);
    setError(null);
    const failure = await requestCode(id).catch(() => 'unavailable' as const);
    setBusy(false);
    if (failure) {
      setError(failure);
      return;
    }
    setTarget(id);
    setCode('');
    setCooldown(RESEND_SECONDS);
  }

  async function onSend(e: FormEvent) {
    e.preventDefault();
    const id = parseIdentifier(raw);
    if (!id) {
      setError('invalid_identifier');
      return;
    }
    await send(id);
  }

  async function onVerify(e: FormEvent) {
    e.preventDefault();
    if (!target) return;
    setBusy(true);
    setError(null);
    const failure = await verifyCode(target, code.trim()).catch(() => 'unavailable' as const);
    setBusy(false);
    if (failure) setError(failure);
    // success: the session listener redirects
  }

  const errorText: Record<string, string> = {
    invalid_identifier: t('login.invalid_identifier'),
    invalid: t('login.invalid_identifier'),
    rate_limited: t('login.rate_limited'),
    send_failed: t('login.send_failed'),
    invalid_code: t('login.invalid_code'),
    unavailable: t('common.error_generic'),
  };
  // The fallback ("send to my email instead") cannot name the address or say there is none: the server answers every
  // request identically so it is not an oracle for who holds an account. Conditional copy + a one-tap way back to
  // WhatsApp, so a user whose account has no email is not left waiting for a code that will never come.
  const sentTo = target?.fallback
    ? t('login.code_sent_email_on_file')
    : target?.channel === 'email'
      ? t('login.code_sent_email', { target: target.email })
      : t('login.code_sent_whatsapp', { target: target?.phone });

  const form = !target ? (
    <form onSubmit={onSend} noValidate>
      {signedOut ? (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="signed-out-reason">
          {t(`login.signed_out_${signedOut}`)}
        </Alert>
      ) : null}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('login.subtitle')}
      </Typography>
      <TextField
        label={t('login.identifier_label')}
        helperText={t('login.identifier_help')}
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        autoComplete="username"
        inputMode="email"
        autoFocus
        fullWidth
      />
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorText[error]}
        </Alert>
      )}
      <Button
        type="submit"
        variant="contained"
        size="large"
        fullWidth
        sx={{ mt: 3 }}
        disabled={busy || !raw.trim()}
      >
        {t('login.send_code')}
      </Button>
    </form>
  ) : (
    <form onSubmit={onVerify} noValidate>
      <Alert severity="info" sx={{ mb: 3 }}>
        {sentTo}
      </Alert>
      <TextField
        label={t('login.code_label')}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        autoComplete="one-time-code"
        inputMode="numeric"
        slotProps={{
          htmlInput: {
            maxLength: 6,
            style: { letterSpacing: 8, fontSize: 24, textAlign: 'center' },
          },
        }}
        autoFocus
        fullWidth
      />
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorText[error]}
        </Alert>
      )}
      <Button
        type="submit"
        variant="contained"
        size="large"
        fullWidth
        sx={{ mt: 3 }}
        disabled={busy || code.length !== 6}
      >
        {t('login.verify')}
      </Button>
      <Stack spacing={1} sx={{ mt: 2 }} alignItems="flex-start">
        <Link
          component="button"
          type="button"
          disabled={cooldown > 0 || busy}
          onClick={() => void send(target)}
          underline="hover"
          sx={{ opacity: cooldown > 0 ? 0.5 : 1 }}
        >
          {cooldown > 0 ? t('login.resend_in', { seconds: cooldown }) : t('login.resend')}
        </Link>
        {target.fallback ? (
          <Link
            component="button"
            type="button"
            disabled={busy}
            underline="hover"
            onClick={() => void send({ channel: 'whatsapp', phone: target.phone! })}
          >
            {t('login.use_whatsapp_instead')}
          </Link>
        ) : (
          target.channel === 'whatsapp' && (
            <Link
              component="button"
              type="button"
              disabled={busy}
              underline="hover"
              onClick={() => void send({ channel: 'email', phone: target.phone!, fallback: true })}
            >
              {t('login.use_email_instead')}
            </Link>
          )
        )}
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={() => {
            setTarget(null);
            setError(null);
          }}
        >
          {t('login.use_other')}
        </Link>
      </Stack>
    </form>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(380px, 5fr) 7fr' },
        bgcolor: 'background.default',
      }}
    >
      <BrandPanel />
      <Box
        component="main"
        sx={{ display: 'flex', flexDirection: 'column', px: { xs: 2, sm: 4 }, py: 2 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <LanguageMenu labelled />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            py: 3,
          }}
        >
          {/* on phones the brand panel is hidden, so the lockup sits above the card */}
          <BrandLockup size="md" sx={{ display: { xs: 'inline-flex', md: 'none' } }} />
          <Card
            variant="outlined"
            sx={{ width: '100%', maxWidth: 420, borderTop: 4, borderTopColor: 'primary.main' }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Typography variant="h5" component="h1" gutterBottom>
                {t('login.title')}
              </Typography>
              {form}
            </CardContent>
          </Card>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textAlign: 'center', display: { md: 'none' } }}
        >
          © {new Date().getFullYear()} Fleetspoke
        </Typography>
      </Box>
    </Box>
  );
}

/** Desktop-only brand side: dark ground in both themes, the dark-ground lockup, tagline and what the product does. */
function BrandPanel() {
  const { t } = useTranslation();
  return (
    <Box
      component="aside"
      sx={{
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 6,
        p: { md: 6, lg: 8 },
        position: 'relative',
        overflow: 'hidden',
        color: '#E6EAEE',
        background: 'linear-gradient(160deg, #1C2630 0%, #14191F 70%)',
        borderRight: 1,
        borderColor: 'divider', // separates the panel from the form side in dark mode
      }}
    >
      {/* the mark, oversized and faint, bleeding off the bottom-right corner */}
      <BrandMark
        size={560}
        tone="dark"
        sx={{
          position: 'absolute',
          right: -150,
          bottom: -110,
          opacity: 0.06,
          pointerEvents: 'none',
        }}
      />
      <BrandLockup size="lg" tone="dark" sx={{ position: 'relative' }} />
      <Box sx={{ position: 'relative', maxWidth: 440 }}>
        <Typography
          component="p"
          sx={{
            fontFamily: BRAND_FONT,
            fontWeight: 700,
            fontSize: { md: 30, lg: 34 },
            lineHeight: 1.2,
            letterSpacing: '-0.01em',
            textWrap: 'balance',
          }}
        >
          {t('login.tagline')}
        </Typography>
        <Stack component="ul" spacing={2} sx={{ listStyle: 'none', p: 0, m: 0, mt: 4 }}>
          <Point icon={<QrCode2Icon fontSize="small" />}>{t('login.point_labels')}</Point>
          <Point icon={<BluetoothSearchingIcon fontSize="small" />}>
            {t('login.point_tracking')}
          </Point>
          <Point icon={<TaskAltIcon fontSize="small" />}>{t('login.point_receipt')}</Point>
        </Stack>
      </Box>
      <Typography variant="caption" sx={{ position: 'relative', color: '#9AA6B1' }}>
        © {new Date().getFullYear()} Fleetspoke
      </Typography>
    </Box>
  );
}

function Point({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <Box component="li" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box
        aria-hidden
        sx={{
          width: 34,
          height: 34,
          flex: 'none',
          display: 'grid',
          placeItems: 'center',
          borderRadius: '50%',
          color: '#F0A248',
          bgcolor: 'rgba(240, 162, 72, 0.14)',
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ color: '#C9D2DA' }}>{children}</Typography>
    </Box>
  );
}
